import React from 'react';
import { Box } from '@mui/material';
import { ZoomIn as ZoomInIcon } from '@mui/icons-material';
import ImageModal from '../../ui/ImageModal';
import ProductImagePlaceholder from '../../ui/ProductImagePlaceholder';
import { isUsableProductImage } from '../../../utils/productImage';

export interface ProductImageData {
  thumbnail?: string | null;
  thumbnailUrl?: string | null;
  imagePath?: string | null;
}

interface ProductImagePreviewProps {
  product: ProductImageData;
  alt: string;
  size?: number;
  borderRadius?: number | string;
  expandable?: boolean;
}

const ProductImagePreview: React.FC<ProductImagePreviewProps> = ({
  product,
  alt,
  size = 64,
  borderRadius = 12,
  expandable = true,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [previewIndex, setPreviewIndex] = React.useState(0);

  const previewSources = [product.thumbnail, product.thumbnailUrl, product.imagePath].filter(
    isUsableProductImage,
  );
  const thumbnailSrc = previewSources[previewIndex];
  const expandedSrc =
    [product.imagePath, product.thumbnail, product.thumbnailUrl].filter(isUsableProductImage)[0] ??
    '';
  const hasImage = Boolean(expandedSrc);
  const canExpand = expandable && hasImage;
  const showPlaceholder = previewIndex >= previewSources.length;

  React.useEffect(() => {
    setPreviewIndex(0);
  }, [product.thumbnail, product.thumbnailUrl, product.imagePath]);

  const imageBoxSx = {
    position: 'relative' as const,
    width: size,
    minWidth: size,
    height: size,
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius,
    overflow: 'hidden',
    backgroundColor: 'grey.100',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 0,
    cursor: canExpand ? 'zoom-in' : 'default',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:disabled': {
      cursor: 'default',
    },
    '& .product-image-preview-overlay': {
      opacity: 0,
    },
    '&:hover': canExpand
      ? {
          transform: 'translateY(-1px)',
          boxShadow: 2,
          '& .product-image-preview-overlay': {
            opacity: 1,
          },
        }
      : undefined,
  };

  return (
    <>
      <Box
        component={expandable ? 'button' : 'div'}
        type={expandable ? 'button' : undefined}
        onClick={canExpand ? () => setIsExpanded(true) : undefined}
        disabled={expandable ? !canExpand : undefined}
        aria-label={
          canExpand
            ? `Expandir imagen de ${alt}`
            : expandable
              ? `Sin imagen disponible para ${alt}`
              : `Imagen de ${alt}`
        }
        sx={imageBoxSx}
      >
        {showPlaceholder ? (
          <ProductImagePlaceholder variant="fill" size={size} showLabel={size >= 56} />
        ) : (
          <Box
            component="img"
            src={thumbnailSrc}
            alt={alt}
            loading="lazy"
            onError={() => setPreviewIndex((currentIndex) => currentIndex + 1)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
        {canExpand && (
          <Box
            className="product-image-preview-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to top, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.05))',
              transition: 'opacity 0.2s ease',
              pointerEvents: 'none',
            }}
          >
            <ZoomInIcon sx={{ color: '#fff', fontSize: Math.max(18, size * 0.32) }} />
          </Box>
        )}
      </Box>

      {expandable && (
        <ImageModal
          isOpen={isExpanded}
          onClose={() => setIsExpanded(false)}
          imageUrl={expandedSrc}
          altText={alt}
        />
      )}
    </>
  );
};

export default ProductImagePreview;
